import { EPageTypes, EPlatformTypes, SimpleCall } from '@cian/frontend-newbuilding-call-component';
import { IBuilder } from '@cian/frontend-newbuilding-call-component/es/types/builder';
import { CianNewbuildingChatButton, CianNewbuildingChatButtonContext } from '@cian/newbuilding-chat-component';
import { Label, UIText1, Avatar, Link, Theme } from '@cian/ui-kit';
import {
  IconActionArrowRight16,
  IconCommunicationMessage16,
  IconCommunicationPhone16,
  IconToggleStarOn16,
} from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';

import { DeveloperHeaderPhone } from './DeveloperHeaderPhone';
import * as style from './style.css';
import { useBuilderChatOpenHandler } from '../../../../../hooks/builderChats';
import { trackPhoneShow } from '../../../../../tracking/newbuilding';
import { IDeveloperInfoRedesigned } from '../../../../../types/developerInfo';
import { useApplicationContext } from '../../../../../utils/applicationContext';
import { numberToPrettyString } from '../../../../../utils/numberToPrettyString';

const LISTING_BUILDERS_HEADER_BLOCK_PLACE_TYPE = 'Open_listing_builders_header_block';

interface IDeveloperInfoProps {
  developerInfo: IDeveloperInfoRedesigned;
}

export const DeveloperInfo: React.FC<IDeveloperInfoProps> = ({ developerInfo }) => {
  const { logger, httpApi } = useApplicationContext();

  const openChat = useBuilderChatOpenHandler({
    placeType: LISTING_BUILDERS_HEADER_BLOCK_PLACE_TYPE,
  });

  const handleShowPhoneClick = React.useCallback(() => {
    trackPhoneShow({
      newbuildingId: developerInfo.id,
      action: LISTING_BUILDERS_HEADER_BLOCK_PLACE_TYPE,
    });
  }, [developerInfo.id]);

  const callBuilders = React.useMemo<IBuilder[]>(() => {
    const phone = parseContactPhone(developerInfo.contact);

    if (!phone) {
      return [];
    }

    return [
      {
        id: developerInfo.id,
        isFichering: false,
        logoUrl: developerInfo.logoUrl || '',
        name: developerInfo.name,
        phone,
        dynamicCalltracking: developerInfo.dynamicCalltracking || {},
        realtyUserId: developerInfo.id,
        showPhone: true,
      },
    ];
  }, [
    developerInfo.contact,
    developerInfo.dynamicCalltracking,
    developerInfo.id,
    developerInfo.logoUrl,
    developerInfo.name,
  ]);

  const chatBuilders = React.useMemo(() => {
    if (!developerInfo.chat?.isEnabled || !developerInfo.chat.entityId || developerInfo.realtyUserId == null) {
      return [];
    }

    return [
      {
        name: developerInfo.name,
        logoUrl: developerInfo.logoUrl || null,
        realtyUserId: developerInfo.realtyUserId,
        chat: developerInfo.chat,
      },
    ];
  }, [developerInfo.chat, developerInfo.logoUrl, developerInfo.name, developerInfo.realtyUserId]);

  const chatButtonContextValue = React.useMemo(
    () => ({
      builders: chatBuilders,
      sellers: [],
    }),
    [chatBuilders],
  );

  const roomLinks = React.useMemo(() => {
    const { flatsStat } = developerInfo;

    if (!flatsStat) {
      return [];
    }

    const links: Array<{ label: string; count: string; url: string }> = [];

    if (flatsStat.total > 0) {
      links.push({
        label: 'Все',
        count: numberToPrettyString(flatsStat.total),
        url: flatsStat.url,
      });
    }

    flatsStat.flats.forEach(flat => {
      links.push({
        label: flat.roomCountText,
        count: numberToPrettyString(flat.count),
        url: flat.url,
      });
    });

    return links;
  }, [developerInfo]);

  return (
    <div className={style['container']} data-testid="DeveloperInfo">
      <div className={style['avatar']}>
        <Avatar src={developerInfo.logoUrl as string} size={80} type="builder" hideBorder />
      </div>

      <div className={style['text-block']}>
        <div className={style['title-row']}>
          <a className={style['title']} href={developerInfo.url} target="_blank" rel="noreferrer">
            {developerInfo.name}
          </a>
          <div className={style['labels']}>
            {developerInfo.rating && (
              <Label
                background="surface-inverted-default"
                color="decorative-orange"
                borderColor="accent-main-secondary"
                borderStyle="solid"
                borderWidth={1}
                icon={<IconToggleStarOn16 color="decorative-orange" />}
              >
                {developerInfo.rating}
              </Label>
            )}
            {developerInfo.reliabilityLabel && (
              <Label
                background="surface-inverted-default"
                color="text-main-default"
                borderColor="accent-main-secondary"
                borderStyle="solid"
                borderWidth={1}
              >
                {developerInfo.reliabilityLabel}
              </Label>
            )}
          </div>
        </div>

        <div className={style['info-row']}>
          {developerInfo.since && <UIText1 color="text-primary-default">{developerInfo.since}</UIText1>}

          <Link
            href={developerInfo.url}
            target="_blank"
            rel="noreferrer"
            fontSize="M"
            color="current_color"
            afterIcon={<IconActionArrowRight16 color="current_color" />}
          >
            Подробнее о застройщике
          </Link>

          {developerInfo.newobjectsStats &&
            developerInfo.newobjectsStats.map((stat, index) => (
              <Link
                key={index}
                href={stat.url as string}
                target="_blank"
                rel="noreferrer"
                fontSize="M"
                color="current_color"
                afterIcon={<IconActionArrowRight16 color="current_color" />}
              >
                {stat.name}
              </Link>
            ))}
        </div>

        {roomLinks.length > 0 && (
          <div className={style['room-links']}>
            {roomLinks.map(link => (
              <a key={link.label} href={link.url} className={style['room-link']}>
                <UIText1 color="current_color">{link.label}</UIText1>
                <UIText1 color="text-secondary-default">{link.count}</UIText1>
              </a>
            ))}
          </div>
        )}
      </div>

      {(callBuilders.length > 0 || chatBuilders.length > 0) && (
        <div className={style['actions']} data-testid="DeveloperInfoActions">
          {callBuilders.length > 0 && (
            <SimpleCall
              SinglePhoneComponent={DeveloperHeaderPhone}
              builders={callBuilders}
              sellers={[]}
              onShowPhoneClick={handleShowPhoneClick}
              singlePhoneButtonText="Показать телефон"
              multiplePhoneButtonText="Показать телефон"
              openContactsStyles={{
                size: 'M',
                theme: 'fill_primary',
                beforeIcon: <IconCommunicationPhone16 color="icon-inverted-default" />,
                fullWidth: true,
              }}
              showPhoneStyles={{
                size: 'M',
                theme: 'fill_primary',
                beforeIcon: <IconCommunicationPhone16 color="icon-inverted-default" />,
                fullWidth: true,
              }}
              httpApi={httpApi}
              logger={logger}
              pageType={EPageTypes.OfferList}
              platformType={EPlatformTypes.WebDesktop}
              placeType={LISTING_BUILDERS_HEADER_BLOCK_PLACE_TYPE}
            />
          )}

          {chatBuilders.length > 0 && (
            <CianNewbuildingChatButtonContext.Provider value={chatButtonContextValue}>
              <Theme theme="onOverlay">
                <CianNewbuildingChatButton
                  onChatOpen={openChat}
                  size="M"
                  theme="fill_secondary"
                  type="button"
                  fullWidth
                  beforeIcon={<IconCommunicationMessage16 color="icon-main-default" />}
                >
                  Написать
                </CianNewbuildingChatButton>
              </Theme>
            </CianNewbuildingChatButtonContext.Provider>
          )}
        </div>
      )}
    </div>
  );
};

function parseContactPhone(contact: string | null | undefined) {
  if (!contact) {
    return null;
  }

  const digits = contact.replace(/\D/g, '');

  if (digits.length < 10) {
    return null;
  }

  if (digits.length === 10) {
    return {
      countryCode: '+7',
      code: digits.slice(0, 3),
      number: digits.slice(3),
    };
  }

  const countryCodeLength = digits.length - 10;

  return {
    countryCode: `+${digits.slice(0, countryCodeLength)}`,
    code: digits.slice(countryCodeLength, countryCodeLength + 3),
    number: digits.slice(countryCodeLength + 3),
  };
}
